import * as _ from 'lodash';

import { finleyAssert } from '@finley-technologies/common';

import {
  GetInstitutionAndConnectionDetailsForBorrowerIdQueryGQLType,
  Maybe,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';

export type BankAccountFormFields = {
  accountholder: string;
  financialInstitution: string;
  connectionProvider: string;
  accountName: string;
  routingNumber: string;
  accountNumber: string;
};

type InstitutionAndConnectionDetailsForBorrower = GetInstitutionAndConnectionDetailsForBorrowerIdQueryGQLType['institutionAndConnectionDetailsForBorrower'];

export const initialBankAccountValues: BankAccountFormFields = {
  accountholder: '',
  financialInstitution: '',
  connectionProvider: '',
  accountName: '',
  routingNumber: '',
  accountNumber: '',
};

export type ConnectionProviderConfig = Record<
  string,
  {
    bankInstitutionId: string;
    connectionProvider: 'teller' | 'plaid';
    connectionItemId: string;
    connectionAccountId: string;
    connectionAccountName: string;
    connectionSelectOptionName: string;
  }
>;

export const getFormattedBankAccountDefaultValues = ({
  organizationId,
  bankInstitutionId,
  accountName,
  routingNumber,
  accountNumber,
  tellerAccountId,
  plaidAccountId,
}: {
  organizationId: string;
  bankInstitutionId: string;
  accountName: string;
  routingNumber: string;
  accountNumber: string;
  tellerAccountId?: Maybe<string>;
  plaidAccountId?: Maybe<string>;
}): BankAccountFormFields => {
  let formattedConnectionProvider: string;
  if (tellerAccountId) {
    formattedConnectionProvider = `teller_${tellerAccountId}`;
  } else if (plaidAccountId) {
    formattedConnectionProvider = `plaid_${plaidAccountId}`;
  } else {
    formattedConnectionProvider = 'None';
  }

  return {
    accountholder: organizationId,
    financialInstitution: bankInstitutionId,
    connectionProvider: formattedConnectionProvider,
    accountName,
    routingNumber,
    accountNumber,
  };
};

export const getConnectionProviderConfig = ({
  institutionAndConnectionDetails,
}: {
  institutionAndConnectionDetails: InstitutionAndConnectionDetailsForBorrower;
}) => {
  const connectionProviderConfig: ConnectionProviderConfig = {};

  _.forEach(
    institutionAndConnectionDetails,
    (institutionAndConnectionDetail) => {
      const {
        tellerBankInstitutionId,
        tellerBankInstitutionName,
        tellerItemId,
        tellerAccountId,
        tellerAccountMask,
        tellerAccountName,
        plaidBankInstitutionId,
        plaidBankInstitutionName,
        plaidItemId,
        plaidAccountId,
        plaidAccountMask,
        plaidAccountName,
      } = institutionAndConnectionDetail;
      if (tellerAccountId && tellerItemId) {
        finleyAssert(
          tellerAccountName && tellerBankInstitutionId,
          'tellerAccountName and bankInstitutionName must exist for a given tellerAccountId',
        );

        const tellerAccountString = `Teller - ${tellerBankInstitutionName} ${tellerAccountName} (${tellerAccountMask})`;

        const connectionProviderConfigKey = `teller_${tellerAccountId}`;

        connectionProviderConfig[connectionProviderConfigKey] = {
          bankInstitutionId: tellerBankInstitutionId,
          connectionProvider: 'teller',
          connectionItemId: tellerItemId,
          connectionAccountId: tellerAccountId,
          connectionAccountName: tellerAccountName,
          connectionSelectOptionName: tellerAccountString,
        };
      }

      if (plaidAccountId && plaidItemId) {
        finleyAssert(
          plaidAccountName && plaidBankInstitutionId,
          'plaidAccountName and bankInstitutionName must exist for a given plaidAccountId',
        );

        const plaidAccountString = `Plaid - ${plaidBankInstitutionName} ${plaidAccountName} (${plaidAccountMask})`;

        const connectionProviderConfigKey = `plaid_${plaidAccountId}`;

        connectionProviderConfig[connectionProviderConfigKey] = {
          bankInstitutionId: plaidBankInstitutionId,
          connectionProvider: 'plaid',
          connectionItemId: plaidItemId,
          connectionAccountId: plaidAccountId,
          connectionAccountName: plaidAccountName,
          connectionSelectOptionName: plaidAccountString,
        };
      }
    },
  );
  return connectionProviderConfig;
};
